import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AuthContext from './context';
import { AuthTokenManager } from './TokenManager';
import AuthService from './services/AuthService';
import { getRoleFromToken, normalizeUserRole } from './utils/roleUtils';

export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  const initialize = useCallback(() => {
    try {
      const token = AuthTokenManager.getAccessToken();
      let userInfo = AuthTokenManager.getUserInfo();
      
      // Check if token exists and is not expired
      if (token && !AuthService.isTokenExpired(token)) {
        // Hydrate role from JWT when missing (older sessions / login without role field)
        if (!userInfo?.role) {
          const roleFromToken = getRoleFromToken(token);
          userInfo = { ...(userInfo || {}), role: roleFromToken };
          AuthTokenManager.setUserInfo(userInfo);
        } else {
          userInfo = { ...userInfo, role: normalizeUserRole(userInfo.role) };
          AuthTokenManager.setUserInfo(userInfo);
        }
        setUser(userInfo || null);
        setLoading(false);
        return true;
      } else {
        // Token is expired or doesn't exist, clear everything
        console.log('🔍 AuthProvider: Token expired or missing during initialization, clearing tokens');
        AuthTokenManager.clearTokens();
        setUser(null);
        setLoading(false);
        return false;
      }
    } catch (e) {
      console.error('🔍 AuthProvider: Initialization error:', e);
      AuthTokenManager.clearTokens();
      setUser(null);
      setLoading(false);
      return false;
    }
  }, []);

  useEffect(() => {
    initialize();

    const onLogout = (event) => {
      console.log('🔍 AuthProvider: Received logout event:', event.detail);
      setUser(null);
      setError(null);
    };

    const onSessionConflict = (event) => {
      console.log('🔍 AuthProvider: Received session conflict event:', event.detail);
      setUser(null);
      setError({
        type: 'SESSION_CONFLICT',
        message: event.detail.message || 'Session conflict detected',
        recoverable: true
      });
    };

    window.addEventListener('auth:logout', onLogout);
    window.addEventListener('auth:sessionConflict', onSessionConflict);
    
    return () => {
      window.removeEventListener('auth:logout', onLogout);
      window.removeEventListener('auth:sessionConflict', onSessionConflict);
    };
  }, [initialize]);

  // Token expiry monitor (Phase 1: backend issues a single 30-min JWT and has no
  // /auth/refresh endpoint). Instead of calling a non-existent refresh endpoint,
  // we keep the session valid for the full token lifetime and clear it once the
  // JWT actually expires, so the user simply re-authenticates.
  useEffect(() => {
    const checkTokenExpiry = () => {
      const token = AuthTokenManager.getAccessToken();
      if (!token) return;

      if (AuthService.isTokenExpired(token)) {
        console.log('🔍 AuthProvider: Token expired, clearing session');
        AuthTokenManager.clearTokens();
        setUser(null);
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Set up interval to check every minute
    const interval = setInterval(checkTokenExpiry, 60000);

    return () => clearInterval(interval);
  }, [user]);

  const login = useCallback(async (authResult) => {
    // authResult expected: { accessToken, refreshToken, expiresIn, user }
    try {
      setError(null);
      setLoading(true);
      AuthTokenManager.setTokens(authResult.accessToken, authResult.refreshToken, authResult.expiresIn);
      AuthTokenManager.setUserInfo(authResult.user || null);
      setUser(authResult.user || null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      console.log('🔍 AuthProvider: Logout started');
      setLoading(true);
      setError(null);
      
      // Call backend logout endpoint
      console.log('🔍 AuthProvider: Calling AuthService.logout()');
      const { data, error } = await AuthService.logout();
      console.log('🔍 AuthProvider: AuthService.logout() response:', { data, error });
      
      if (error) {
        console.warn('Backend logout failed, but clearing local tokens:', error.message);
      }
      
      // Always clear local tokens regardless of backend response
      console.log('🔍 AuthProvider: Clearing local tokens');
      AuthTokenManager.clearTokens();
      setUser(null);
      
      // Dispatch logout event for other components
      console.log('🔍 AuthProvider: Dispatching logout event');
      window.dispatchEvent(new CustomEvent('auth:logout', {
        detail: { success: true, localOnly: error ? true : false }
      }));
      
      console.log('🔍 AuthProvider: Logout completed successfully');
    } catch (e) {
      console.error('Logout error:', e);
      // Even if logout fails, clear local tokens
      AuthTokenManager.clearTokens();
      setUser(null);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      setError(null);
      const newToken = await AuthTokenManager.refreshToken();
      return newToken;
    } catch (e) {
      setError(e);
      return null;
    }
  }, []);

  const getToken = useCallback(() => AuthTokenManager.getAccessToken(), []);

  const isAuthenticated = useMemo(() => {
    const token = AuthTokenManager.getAccessToken();
    console.log('🔍 AuthProvider: Checking authentication status, token exists:', !!token);
    
    if (!token) {
      console.log('🔍 AuthProvider: No token found, user not authenticated');
      return false;
    }
    
    // Check if token is expired
    const isExpired = AuthService.isTokenExpired(token);
    console.log('🔍 AuthProvider: Token expired:', isExpired);
    
    if (isExpired) {
      console.log('🔍 AuthProvider: Token is expired, clearing tokens');
      AuthTokenManager.clearTokens();
      setUser(null);
      return false;
    }
    
    console.log('🔍 AuthProvider: Token is valid, user is authenticated');
    return true;
  }, [user]);

  const value = useMemo(() => ({
    isAuthenticated,
    user,
    loading,
    error,
    login,
    logout,
    refresh,
    getToken,
  }), [isAuthenticated, user, loading, error, login, logout, refresh, getToken]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;


